import * as Yup from 'yup';
import { isEmpty } from 'lodash-es';
import { FormikErrors, FormikTouched } from 'formik';
import { Props } from '@service-victoria/component-library/components/AddressLookup';
import { OptionType } from '@service-victoria/component-library/components/AutoComplete/props';
import { constants } from 'utils';
import { CovidAddress } from 'interfaces/common';
import { doesNotContainEmoji } from 'utils/validations';

function getDetailsTouched(touched: boolean): FormikTouched<Props.Interfaces.FullDetails> {
  return {
    fullAddress: touched,
    postCode: touched,
    state: touched,
    suburb: touched
  };
}

function getDetailsErrors(error: string): FormikErrors<Props.Interfaces.FullDetails> {
  return {
    fullAddress: error,
    postCode: error,
    state: error,
    suburb: error
  };
}

/**
 * Initial values object for <Address /> element
 */
export const initialAddressValues: Props.Interfaces.AddressData = {
  summaryData: {
    label: '',
    value: ''
  },
  fullDetails: { id: '', fullAddress: '', postCode: '', state: '', suburb: '' }
};

/**
 * Initial touched object for full <Address /> element
 * @param touched touched value to populate
 */
export const initialAddressTouched = (touched: boolean) => {
  return {
    fullDetails: getDetailsTouched(touched),
    /**
     * This can be of OptionType/number/string, hence we store a single boolean value in it for consistency across all different cases
     */
    summaryData: touched
  };
};

/**
 * Initial error object for <Address /> element
 * @param error error to populate
 */
export const initialAddressErrors = (error: string) => {
  return {
    fullDetails: getDetailsErrors(error),
    summaryData: error
  };
};

/**
 * Manual type values
 */
export type ManualValues = AddressFormValues;

/**
 * <Formik /> address type values defintion for a "single" type
 */
export type AddressFormValues = {
  address: Props.Interfaces.AddressData;
};

/**
 * standard <Formik /> values type definition for an array of <Address /> elements
 */
export type ArrayFormValues = {
  addresses: Props.Interfaces.AddressData[];
};

/**
 *  Checks whether an address is PO Box. Returns true the string contains "po box"
 */
const isPostOfficeBox = (addressString: string) => {
  const cleanedAddressString = addressString.toLowerCase().replace(/[^\w\s]/gi, '');
  return cleanedAddressString.indexOf('po box') >= 0;
};

/**
 * Address validation schema helpers
 * Prevents PO Boxes from being accepted
 */
export const addressSchema = Yup.object().shape<CovidAddress>({
  isOverseasAddress: Yup.boolean(),
  overseas: Yup.mixed().when('isOverseasAddress', {
    is: true,
    then: Yup.object().shape<CovidAddress['overseas']>({
      country: Yup.string().required(constants.GenericValidationErrors.genericError),
      info: Yup.object().shape<CovidAddress['overseas']['info']>({
        basic: Yup.string()
          .required(constants.GenericValidationErrors.required)
          .max(120, constants.AddressValidationErrors.overseasAddressCharacterLimit)
          .test(
            'isOverseasAddressValid',
            constants.GenericValidationErrors.genericError,
            doesNotContainEmoji
          )
      })
    }),
    otherwise: Yup.object().notRequired()
  }),
  fullDetails: Yup.mixed().when('isOverseasAddress', {
    is: false,
    then: Yup.object().shape<CovidAddress['fullDetails']>({
      id: Yup.string().notRequired(),
      fullAddress: Yup.string()
        .required(constants.AddressValidationErrors.place)
        .test(
          'isPostOfficeBox',
          constants.AddressValidationErrors.noPostBox,
          function validate(address) {
            if (address) {
              return !isPostOfficeBox(address);
            } else {
              return true;
            }
          }
        )
        .test('isFullAddressValid', constants.AddressValidationErrors.place, doesNotContainEmoji),
      postCode: Yup.string()
        .required(constants.AddressValidationErrors.place)
        .matches(constants.REGEX.POSTCODE, constants.AddressValidationErrors.postcode)
        .max(4, constants.AddressValidationErrors.place),
      state: Yup.string().required(constants.AddressValidationErrors.place),
      suburb: Yup.string()
        .required(constants.AddressValidationErrors.place)
        .matches(constants.REGEX.SUBURB, constants.AddressValidationErrors.suburb)
    }),
    otherwise: Yup.object().notRequired()
  }),
  summaryData: Yup.mixed().when(['fullDetails', 'isOverseasAddress'], {
    is: (fullDetails, isOverseasAddress) => {
      const isManualAddress =
        isEmpty(fullDetails.id) &&
        !isEmpty(fullDetails.fullAddress) &&
        !isEmpty(fullDetails.postCode) &&
        !isEmpty(fullDetails.state) &&
        !isEmpty(fullDetails.suburb);

      return isOverseasAddress || isManualAddress;
    },
    then: Yup.object().notRequired(),
    otherwise: Yup.mixed()
      .test(
        'emptyValue',
        constants.AddressValidationErrors.place,
        function validate(address?: OptionType) {
          return address ? !isEmpty(address?.label) : false;
        }
      )
      .test(
        'no value selected from drop down',
        constants.AddressValidationErrors.pickAddress,
        function validate(address: OptionType) {
          return !isEmpty(address?.label) && !isEmpty(address?.value);
        }
      )
      .test(
        'isPostOfficeBox',
        constants.AddressValidationErrors.noPostBox,
        function validate(address: OptionType) {
          if (address?.label) {
            return !isPostOfficeBox(address?.label);
          } else {
            return true;
          }
        }
      )
  })
});
